/**
 * What Sky knows about the platform she lives in.
 *
 * Written from the product itself rather than invented: each module's account
 * is the screen's own title and subtitle, and each term is the rule the domain
 * code actually implements — grade thresholds from `domain/kpis.ts`, statuses
 * from `domain/workOrders.ts`, attention from `domain/library.ts`, the wire
 * facts from ARCHITECTURE.md. That sourcing is the point. An assistant that
 * explains a field from a plausible guess is worse than one that says it does
 * not know, because a confident wrong explanation of an indicator is how an
 * operator comes to misread it for months.
 *
 * Two consequences of writing it this way:
 *
 *  - When a screen changes, this goes stale, and stale is the failure mode to
 *    fear. Every entry names where it came from so the next person can check
 *    it against the source rather than against their memory.
 *
 *  - It covers what the platform models, not what an airport does. She explains
 *    what "NOT COMPUTABLE" means here and why it is not zero; she does not
 *    explain what friction testing is for. The second is the operator's trade
 *    and they know it better than she does.
 */

export interface Module {
  id: string;
  name: string;
  route: string;
  /** The sidebar group, so the tour walks the app the way the app is laid out. */
  group: string;
  /** One line: what the screen is for, in the product's own words. */
  what: string;
  /** How it actually works — the mechanism, not the marketing. */
  how: string;
  /** What to look at first. */
  look?: string;
}

/*
 * Ordered as the sidebar orders them, because a tour that jumps around is a
 * tour of a different application than the one on screen.
 */
export const MODULES: readonly Module[] = [
  {
    id: "dashboard",
    name: "Dashboard",
    route: "/",
    group: "Overview",
    what: "The state of the airport in one screen.",
    how: "Executive KPIs, each against a shown target, graded, with its source; then open work by priority and age, open alerts by severity, and the work-order lifecycle counted by state. Targets are shown rather than hidden so nobody is graded against a number they have never seen.",
    look: "The grade in the corner is the share of measures on target. A measure the platform cannot compute is excluded, not counted as zero.",
  },
  {
    id: "airfield",
    name: "Dynamic map",
    route: "/airfield",
    group: "Operations control",
    what: "The aerodrome drawn, with work and alerts placed on it.",
    how: "Layers come from the platform as map features; each open work order and alert carries a point the platform computed, so what you see on the plan is the record itself rather than a pin somebody dropped.",
  },
  {
    id: "work-orders",
    name: "Work orders",
    route: "/work-orders",
    group: "Operations control",
    what: "Everything raised, and where each of it stands.",
    how: "Eight statuses — draft, approved, dispatched, in progress, on hold, completed, verified, cancelled — and only legal moves are offered. The platform has the final word: a refusal for missing evidence or segregation of duties is shown exactly as the platform worded it.",
    look: "Priority is an integer 1–5 on the record, shown as P1–P5. P1 is safety critical. Filter by date range, repair type and discipline; the statistics, the printable report and the CSV export all cover exactly the orders the filters leave.",
  },
  {
    id: "alerts",
    name: "Alerts",
    route: "/alerts",
    group: "Operations control",
    what: "What the rules raised, and why.",
    how: "Open alerts only. “Why did this fire?” joins the rule and its evaluation and shows the expression, the parameters, what they resolved to, and the work orders the alert raised. Predictive alerts carry a confidence and the date the fault is expected.",
    look: "An alert with no work order raised from it is the most actionable thing on the screen.",
  },
  {
    id: "inspections",
    name: "Inspections",
    route: "/inspections",
    group: "Operations control",
    what: "Sheets from the field, and what they found.",
    how: "The pack decides what an inspection judges and by what value. Items that fail carry forward as defects — closing the sheet does not close them. Sheets captured without signal are marked as synced later.",
  },
  {
    id: "compliance",
    name: "Compliance",
    route: "/compliance",
    group: "Safety & compliance",
    what: "Obligations and the rules that test them.",
    how: "Every period is listed whether or not it was met, so what is missing shows rather than being absent. A gated rule produces no alerts until its thresholds are confirmed against the certification basis.",
  },
  {
    id: "library",
    name: "Library",
    route: "/library",
    group: "Operations control",
    what: "The documents, their revisions and their dates.",
    how: "A document in force is one whose status is issued. Expiry and review dates drive the attention state: expired, expires within 30 days, review overdue, review due within 30 days.",
    look: "Sky watches these dates and warns at 30, 15 and 7 days, then once more if the date passes.",
  },
  {
    id: "intelligence-report",
    name: "Intelligence report",
    route: "/intelligence-report",
    group: "Operations control",
    what: "What is going to break, what it will cost, and who holds it — with the evidence and a defensibility grade on each finding.",
    how: "Each finding comes from the platform's own records: predictive alerts with the rule that raised them, overdue work with its crew, mandatory tests and obligations with their dates, wear-out fits, the records ledger and project hold points. Cost comes from the deferred-cost register or the work order; nothing is priced by guesswork. Strong means dated records and a verified rule; partial means something is missing; weak means an unverified threshold, a drifted model or a demonstration record.",
    look: "Print for the board gives the report alone, on white, with the appendix of every record cited.",
  },
  {
    id: "d-brief",
    name: "D-Brief",
    route: "/d-brief",
    group: "Operations control",
    what: "The exception brief: three things that will degrade capacity in the next thirty days, each with confidence, closure minutes, remote-stand penalty and the decision to make this week — and a question box over the evidence.",
    how: "The three are the intelligence report's findings that touch a runway, taxiway, stand or apron, weighed by how soon and how badly. Closure minutes are the median of that zone's closures on record; the remote-stand penalty is the capacity model's answer for a closure tomorrow at the usual hour. The question box finds the closure by zone and day, then attaches the work order it was declared for, the inspection around it, the photographs on the work, and the clause behind the rule that fired.",
    look: "Regenerate recomputes it now; the change feed refreshes it when the estate moves. Print gives the brief alone.",
  },
  {
    id: "decision-assist",
    name: "Decision Room",
    route: "/decision-assist",
    group: "Executive",
    what: "The decisions the records put on the chief executive's desk, nearest decide-by first: the runway below its limit, next year's capital envelope, readiness for the peak, the regulator, the quarter's windows. Each opens into the stakes, the options side by side, a stress test, the flip points, three lines for the board and a recorded decision.",
    how: "Every option is ranked on cash-equivalent — cash out plus closure hours at the rate card — and tested against a peak, a 20% cut, an unplanned failure, the regulator arriving and a 30-day slip; the robust option is the one with the best worst case. Beneath it the engine is deterministic: consequence from closure minutes and movements, time to threshold from the condition trend, regulatory exposure from the distance to the clause limit, the deferral cost curve from the register or the rate card, and the window constraint from the quarter's scarcity. Anything past a Part 139 limit is mandatory and removed from the trade-off. The solver is a three-constraint knapsack. A connected model may explain the result and is checked figure by figure against the engine; text with a figure the engine did not produce is withheld.",
    look: "Open a decision from the desk; enter the peak and inspection dates and any estimate the platform lacks in the assumptions drawer; record the decision with its rationale. The engine is one link away for whoever checks the arithmetic.",
  },
  {
    id: "urgent-inspection",
    name: "Urgent Inspection",
    route: "/urgent-inspection",
    group: "Aerodrome",
    what: "The mandatory inspection of the movement area after bad weather — heavy rain, a sandstorm, snow or ice, hail or a strong wind — before operations are confirmed safe: each runway in thirds, each taxiway, each apron.",
    how: "Each runway third takes its surface condition, depth and coverage, and the runway condition code follows the ICAO assessment matrix; an assessor may lower a code on observation, never raise it. Taxiways and aprons are a checklist — standing water, sand, FOD and flying debris, surface damage, lights and signs, markings, drainage, and on the aprons equipment moved from its secured place — each clear, hazard or cleared. Every check is appended, never overwritten. Operations are confirmed only when every area is inspected and no hazard is open; otherwise the inspection closes with the restricted areas named. The SNOWTAM, the FAA FICON NOTAM, the Part 139 self-inspection record and the inspection report are filled from the record, submitted to a named recipient and kept as exact copies.",
    look: "Start an inspection after the weather, pick each area to record its check, create a new work order for a hazard, confirm or close with restrictions, then submit the forms.",
  },
  {
    id: "crisis",
    name: "Crisis Desk",
    route: "/crisis",
    group: "Executive",
    what: "Whether the airport is ready for the day something breaks: six playbooks with who holds the first quarter hour, the first actions, and what the platform must show for the airport to be ready — and does.",
    how: "Readiness is read from the records: the emergency exercise obligation, the open severity-5 alerts, the equipment out of service, the capacity model's critical positions, the documents in the library. Each gap names the record that would close it. The drill prices a scenario on the capacity model where it covers the window, else the rate card, and says which.",
    look: "The least-ready playbook is marked drill first; record each drill with what was learned.",
  },
  {
    id: "cash",
    name: "Cash & Bottom Line",
    route: "/cash",
    group: "Executive",
    what: "Income and burn a day, net against plan, the bridge from the plan to the actual, cost by area, what work cost on the platform, and what is on the table — cuts, recoveries and escalation avoided — each with its risk.",
    how: "Revenue and operating cost are the finance system's figures, entered and marked; what work cost, what deferral costs and what the projects spent are the platform's. Candidates are computed: lines under plan annualised, areas over budget, work-order overruns, the deferred-cost register's escalation, unaccounted energy against the energy line.",
    look: "Every tile on demonstration figures is dashed and says so; enter the finance figures in the drawer.",
  },
  {
    id: "capacity-expansion",
    name: "Capacity & Expansion",
    route: "/capacity-expansion",
    group: "Executive",
    what: "When the airport runs out of room, on which constraint first, and what each way of adding room costs per passenger it admits.",
    how: "Demand and design capacity are entered; saturation is the year each constraint is reached at the entered growth, and at a third less and more. Options carry starting figures the airport replaces, and each has a start-by year worked back from saturation by its build time.",
    look: "The binding constraint is named; the recommended option is the cheapest per passenger that relieves it.",
  },
  {
    id: "capex",
    name: "Capital Projects",
    route: "/capex",
    group: "Executive",
    what: "The capital portfolio in numbers and one verdict per project: press on, watch, rebaseline or hold.",
    how: "From the platform's projects: money, schedule slip, gates, hold points. Spend ahead of the calendar is spent-percent less elapsed-percent; a project stopped at a hold point is held; more than thirty days late is a rebaseline.",
    look: "Worst first; the finance system's capital lines beneath, marked demonstration until entered.",
  },
  {
    id: "regulatory",
    name: "Regulatory Demands",
    route: "/regulatory",
    group: "Executive",
    what: "Readiness by framework, the ninety-day calendar, the limits crossed, the rules unverified, the undertakings given, and the cost to close every finding.",
    how: "Obligations and readiness from the compliance register; certificates and their expiry from the library; crossed limits from severity-5 reactive alerts and the rule's reference; cost to close from the rate card by category.",
    look: "Enter the inspection date and record each undertaking with its date and owner; a missed one is a finding.",
  },
  {
    id: "benchmark",
    name: "Benchmark",
    route: "/benchmark",
    group: "Executive",
    what: "Where the airport stands on thirteen metrics against local, regional and global peers, ranked only among peers with an entered value.",
    how: "Own figures from the KPIs, the finance figures and the green programme. Peer figures are never estimated: they are entered from published sources with the year and the source, and a blank stays a blank.",
    look: "Switch the peer set; enter a peer's published figure in the drawer.",
  },
  {
    id: "improvement",
    name: "Business Improvement",
    route: "/improvement",
    group: "Executive",
    what: "Where the operation leaks value, what each leak is worth, and the initiatives that close them, ranked by payback.",
    how: "Signals from the platform — KPIs off target, overdue work, unplanned closures, revenue under plan — priced only where a rate exists: the rate card, the finance figures, or the entered value of a KPI point. Unpriced initiatives are listed with the value blank.",
    look: "Enter what a point of each KPI is worth and add your own initiatives in the drawer.",
  },
  {
    id: "green-program",
    name: "Green & LEED",
    route: "/green-program",
    group: "Executive",
    what: "The carbon inventory, the intensities, the ACA ladder as a checklist, the trajectory, the peers, a plan of measures per tonne, the anomalies, and a LEED self-assessment.",
    how: "A deterministic engine on the meter series and entered quantities, with every factor visible and every result on a starting factor marked. The ACA level is the highest whose requirements are all met — computed, documented in the library, or entered with evidence.",
    look: "Enter passengers, fuel, waste, the baseline and the target in the drawer; the LEED points are self-assessed by category.",
  },
  {
    id: "think",
    name: "Ask",
    route: "/think",
    group: "Deep thinking",
    what: "Plain questions about the work, the money, the windows and the forecasts, answered from the platform's own engines.",
    how: "Each card asks one question and answers it from the same engine its room uses, with how sure the answer is (the share of the record the engine could reason about) and what would change it. Open the working, ask me to explain it, or record the decision — it goes into the Decision Room's record.",
    look: "The yellow strip at the top lists the answers that need attention. 'Can't say' is an answer: it means a fact is missing, and the card names it.",
  },
  {
    id: "decide",
    name: "Decide the programme",
    route: "/decide",
    group: "Deep thinking",
    what: "The year's program, the cost of waiting, and what a change would move — three tabs.",
    how: "The plan tab ranks what the budget buys and lists what could not be decided and who can unblock it. Cost of waiting prices deferral at each budget level. Try a change re-runs the programme with more money or more crews and shows what moves — and says so plainly when nothing does.",
  },
  {
    id: "window",
    name: "Find a window",
    route: "/window",
    group: "Deep thinking",
    what: "When work can happen, whether the weather allows it, and what closing a stand costs — three tabs.",
    how: "When to work joins a traffic answer and a weather answer for a position and a job. Weather checks measured conditions against the work's limits and keeps 'cannot be determined' apart from 'allowed'. Closing a stand shows where each affected flight would go and which stands cannot close at all.",
  },
  {
    id: "trust",
    name: "Can we trust it",
    route: "/trust",
    group: "Deep thinking",
    what: "Whether the forecasts still hold, where records disagree, and how assets really fail — three tabs.",
    how: "The forecasts tab keeps three questions apart — inputs moved, calibration, measured performance — and treats 'cannot say yet' as its own state. Disagreements tells a process difference from noise. How things fail fits each population with the units still running kept in.",
  },
  {
    id: "programme",
    name: "What to do next",
    route: "/programme",
    group: "Deep thinking",
    what: "The work program for a fiscal year against an envelope.",
    how: "It ranks what the money buys — and gives equal standing to the decisions the platform declined to make, grouped by the desk that would unblock them and valued in money. Work held up by a missing fact rather than by budget is called out separately, because that is the part more money will not fix.",
  },
  {
    id: "planning",
    name: "When to do the work",
    route: "/planning",
    group: "Deep thinking",
    what: "A window for a job, given traffic and weather.",
    how: "Traffic and weather are answered separately and joined here. Both must be answerable or there is no plan — the join is only as good as its weaker half, so both halves are shown. It needs three things: the position, the category of work, and how many hours it needs.",
    look: "“Cannot say” and “no window” are different answers. The first means half the data is missing; the second means every candidate is closed.",
  },
  {
    id: "counterfactual",
    name: "What if",
    route: "/what-if",
    group: "Deep thinking",
    what: "What moves if you change a lever.",
    how: "Levers are applied to the platform's own model and the difference is reported. When nothing moves it says so plainly rather than showing a flat chart that looks like an answer.",
  },
  {
    id: "reliability",
    name: "How things fail",
    route: "/reliability",
    group: "Deep thinking",
    what: "How the fleet actually fails, fitted from the records.",
    how: "Populations carry both units and failures. The gap between them is what a failures-only fit throws away — fit only the things that broke and you learn how broken things break, not how the fleet does.",
  },
  {
    id: "ledger",
    name: "Disagreements",
    route: "/ledger",
    group: "Deep thinking",
    what: "Where the parties' records diverge.",
    how: "Only subjects both parties reported can diverge; the rest is one party talking, and is shown as that rather than as a dispute.",
  },
  {
    id: "liability",
    name: "What deferring maintenance costs",
    route: "/liability",
    group: "Deep thinking",
    what: "The cost of not doing the work, by fiscal year.",
    how: "Each envelope is a budget you might be given; what changes between them is which candidates slip. The register behind the curve is the list of what is slipping.",
  },
  {
    id: "capacity",
    name: "What closing a stand costs",
    route: "/capacity",
    group: "Deep thinking",
    what: "The traffic cost of a closure.",
    how: "The cost of a closure is the traffic that has nowhere else to go, not the traffic it carries. The screen shows where each movement would go, because the reason is the answer and the destination is a consequence of it.",
  },
  {
    id: "drift",
    name: "Model drift",
    route: "/drift",
    group: "Deep thinking",
    what: "Whether the models still describe the estate.",
    how: "Checks run on a schedule and compare what the model expects against what the records now say. A model that was right last year and is wrong now fails quietly otherwise.",
  },
  {
    id: "projects",
    name: "Capital projects",
    route: "/capital-projects",
    group: "Operations control",
    what: "Projects, their stage gates and their hold points.",
    how: "The gate vocabulary is pack content, published by the platform — nothing in the app hard-codes a gate model. Releasing a hold point is a separate act from recording an inspection result, separately permissioned and audited every time.",
    look: "Money on a project is as declared. The platform computes nothing from it, and neither does the screen.",
  },
  {
    id: "weather",
    name: "Weather",
    route: "/weather",
    group: "Deep thinking",
    what: "The conditions the planning join uses.",
    how: "The same weather half that answers “when to do the work”, shown on its own so a refused window can be traced to the limit that refused it.",
  },
  {
    id: "migration",
    name: "Replacing a system",
    route: "/migration",
    group: "Platform",
    what: "The system this platform is replacing, screen by screen, and the evidence each cutover rests on.",
    how: "Every legacy screen declared here is mapped onto a public endpoint of this platform. The existing application keeps answering; it posts what it was about to return, the platform computes its own answer and the two are compared. Agreement counts only what the contract could compare, so a contract that compares nothing cannot report a hundred per cent. A cutover is refused until the screen has enough observations, agreement above its own tolerance, every difference triaged and a conformance run behind it — and the refusal says which of those is missing. An override is allowed with a written reason and is recorded as an override.",
    look: "The fraction is of the screens declared here, not of the existing application's screens; nobody has counted those. Reverting has no guard: undoing a cutover is one call and needs no deploy.",
  },
  {
    id: "administration",
    name: "Administration",
    route: "/administration",
    group: "Platform",
    what: "Who is here, what they may do, how they get in, and what this deployment still needs.",
    how: "Four tabs. Setup is a checklist computed from the platform's own database on every read, so a step cannot be ticked while it is not true and it un-ticks itself if somebody undoes the thing; a step marked blocking means unsafe or unusable rather than merely unfinished. People lists everybody with the roles they hold and the level those roles add up to, and adds somebody — no email is sent, because the platform has no inbox; what is created is the row their identity provider's subject binds to. Roles shows what each offers its holders and lets a tenant's own roles be set; a role the platform ships is shared by every tenant on the deployment and cannot be edited here. Sign-in says how the deployment authenticates and warns when the development header is on.",
    look: "An access level decides what somebody is SHOWN and what I will discuss with them. It never grants a permission — every endpoint still checks its own key, so a level set too high shows a screen whose data the platform then refuses.",
  },
  {
    id: "calls",
    name: "Staff calls",
    route: "/calls",
    group: "Platform",
    what: "Voice and video calls between staff, one to one or in groups, inside the platform and the mobile app.",
    how: "The pictures and sound go directly between the browsers and phones on the call (WebRTC, one connection per pair of people, up to eight on a call); the platform carries only the setting-up messages and the record, and decides who may call whom. The call window sits above every screen, so a call carries on while you move around the platform: minimise it to a pill, drag it, expand it, or pop it out to stay above every window. Each person's stored numbers are also there to dial by telephone, and the contact directory's people with no sign-in are listed as phone only.",
    look: "A greyed Voice or Video button says why underneath — usually the rule on Who can call whom, or the person is on Do not disturb. Each person picks a ringtone and may turn on Do not disturb for an hour, until 07:00 tomorrow or until they turn it off, with a list of people who still ring through. Meetings are scheduled with reminders and started by the organiser; a group call is invited only, has a waiting room (ask to join), or is open. In a call: raise a hand, chat, share a screen (not from a phone), and as host or co-host admit, mute, remove or end for all. Calls across different networks need a relay (TURN) server; that screen says whether one is configured.",
  },
  {
    id: "call-policy",
    name: "Who can call whom",
    route: "/call-policy",
    group: "Platform",
    what: "The administrator's rule for staff calls.",
    how: "Open by default. A four-by-four matrix of access levels says who may call whom; each level may or may not use video and start group calls; a call holds two to eight people and rings for fifteen to a hundred and twenty seconds. Exceptions name a person, role, level or anyone at each end and are checked first — a deny beats an allow. Chosen levels may break through Do not disturb with an urgent call (none can by default). The checker explains any pairing before the change is saved.",
  },
];

/* ── the vocabulary ───────────────────────────────────────────────────── */

export interface Term {
  id: string;
  /** Everything an operator might call it. Matched case-insensitively. */
  aliases: readonly string[];
  name: string;
  what: string;
  how: string;
  /** Where the rule actually lives, so anybody can check this is still true. */
  source: string;
}

export const TERMS: readonly Term[] = [
  {
    id: "not-computable",
    aliases: ["not computable", "uncomputable", "cannot compute", "no value"],
    name: "NOT COMPUTABLE",
    what: "A measure the platform could not work out, shown with the reason.",
    how: "It is never shown as zero. A zero there reads as good news and is a lie — “no serious incidents” and “we could not count incidents” are opposite facts. It is also excluded from the overall grade rather than counted as a failure.",
    source: "domain/kpis.ts, and rule 4 of the architecture: nothing pretends.",
  },
  {
    id: "grade",
    aliases: ["grade", "a b c d f", "kpi grade", "letter"],
    name: "The A–F grade",
    what: "How a measure did against its own target.",
    how: "It is the ratio of achievement to target: A at or above target, B from 0.95, C from 0.85, D from 0.75, F below. Which way is better comes from the measure's own direction, so a lower-is-better measure is not penalised for being small.",
    source: "gradeFromRatio in domain/kpis.ts.",
  },
  {
    id: "readiness",
    aliases: ["go", "no-go", "nogo", "caution", "readiness", "idle", "state pill"],
    name: "GO / CAUTION / NO-GO / NO DATA",
    what: "The readiness pill beside a measure.",
    how: "GO is within limits, CAUTION is worth watching, NO-GO is outside them. The fourth state is idle — no data — and it is deliberately distinct from NO-GO, because nothing being known is not the same as something being wrong.",
    source: "ReadinessState in domain/kpis.ts.",
  },
  {
    id: "priority",
    aliases: ["priority", "p1", "p2", "p3", "p4", "p5"],
    name: "Priority P1–P5",
    what: "How urgent a work order is.",
    how: "An integer 1–5 on the record, shown as P1–P5. 1 is safety critical, 5 is routine. It is the platform's own field — Sky will read “P2” from an instruction but will not turn “urgent” into a number, because a priority moves a crew.",
    source: "WORK_ORDER_PRIORITIES in domain/workOrders.ts.",
  },
  {
    id: "statuses",
    aliases: ["status", "statuses", "lifecycle", "draft", "dispatched", "on hold", "verified"],
    name: "The work-order lifecycle",
    what: "The eight states a work order can be in.",
    how: "Draft, approved, dispatched, in progress, on hold, completed, verified, cancelled. Open means any of the first five. Only legal moves are offered, and the platform decides — the screen disables what your permissions lack as a courtesy, and the platform enforces it regardless.",
    source: "WORK_ORDER_STATUSES and isOpen in domain/workOrders.ts.",
  },
  {
    id: "severity",
    aliases: ["severity", "sev", "alert severity"],
    name: "Alert severity 1–5",
    what: "How bad the rule says it is.",
    how: "1 is low, 5 is critical, as the rule pack declared it. Severity is the rule's judgement, not the platform's — two tenants running different packs can rate the same condition differently, which is why the rule and its expression are one click away.",
    source: "Alert.severity in domain/alerts.ts.",
  },
  {
    id: "kind",
    aliases: ["kind", "corrective", "preventive", "predictive", "inspection kind"],
    name: "Work-order kind",
    what: "Why the work exists.",
    how: "Corrective, preventive, predictive, project or inspection. It is not the trade and not the task. The kind of repair is a separate column on the work orders screen: the asset type the order was raised against, else the defect type it closes, and “Not classified” when it was raised against neither — never guessed from the title.",
    source: "WORK_ORDER_KINDS in domain/workOrders.ts; repairTypeOf in domain/workOrderReport.ts.",
  },
  {
    id: "attention",
    aliases: ["attention", "expiring", "expired", "review due", "review overdue"],
    name: "Document attention",
    what: "Whether a document needs looking at.",
    how: "Only documents that are issued are in force, so only those are assessed. Expired, expires within 30 days, review overdue, review due within 30 days — anything else is fine. Sky warns on the same dates at 30, 15 and 7 days, and once more if the date passes.",
    source: "attention() in domain/library.ts.",
  },
  {
    id: "sample-data",
    aliases: ["sample data", "sample", "mock", "not real", "built-in sample"],
    name: "The SAMPLE DATA band",
    what: "This session is not showing your platform.",
    how: "It means the adapter in use is the built-in sample, either because no platform is configured or because the one configured could not be reached. Nothing on the screen is real while it shows, and a thin real answer is never topped up with sample rows to look fuller.",
    source: "AppShell.tsx, and rule 4 of the architecture.",
  },
  {
    id: "predictive",
    aliases: ["predictive", "confidence", "predicted for", "confirmed"],
    name: "Predictive alerts",
    what: "A fault the model expects before it happens.",
    how: "It carries a confidence between 0 and 1 and the date the fault is expected. It is confirmed by the work order completing and refuted by it being cancelled — so the precision figure on the dashboard is measured against what actually happened, not asserted.",
    source: "AlertExplanation in domain/alerts.ts.",
  },
  {
    id: "transition-log",
    aliases: ["transition log", "history", "audit", "who did"],
    name: "The transition log",
    what: "The append-only record of every move.",
    how: "Who, when, from what, to what, and the note they left. It is what segregation of duties is checked against. Sky never appears in it — she prepares, you press, and the log names you.",
    source: "WorkOrderEvent in domain/workOrders.ts.",
  },
  {
    id: "permissions",
    aliases: ["permission", "permissions", "access", "access level", "can i"],
    name: "Permission keys",
    what: "What your sign-in is allowed to read and do.",
    how: "Keys like site:read, work_order:read|write|verify, document:read|write|approve, insight:read, rule:verify. The screen disables what you lack and names the key; the platform enforces it whatever the screen does. Sky obeys the same keys — she will not warn you about a document you cannot read.",
    source: "PERMISSION in api/dataSource.ts.",
  },
];

/* ── finding the right answer ─────────────────────────────────────────── */

export function isTourRequest(q: string): boolean {
  const t = q.toLowerCase().trim();
  return (
    /\b(tutorial|walk\s?through|walk me through|show me around|give me a tour|teach me|orient)\b/.test(t) ||
    /*
     * The noun is required. Without it "what does this say" — a question about
     * the screen in front of the operator — was being answered with a tour of
     * the whole application.
     */
    /^what (can|does) (this|the) (app|platform|system|module)\b/.test(t) ||
    /^how does (this|the) (app|platform|system)\b/.test(t)
  );
}

export function isExplainRequest(q: string): boolean {
  const t = q.toLowerCase().trim();
  return /^(what|what's|whats) (is|are|does|do)\b/.test(t) || /^(explain|tell me about|what do you mean by)\b/.test(t);
}

/** The term an operator asked about, or null. Longest alias first, so "review due" beats "review". */
export function findTerm(q: string): Term | null {
  const t = q.toLowerCase();
  let best: { term: Term; length: number } | null = null;
  for (const term of TERMS) {
    for (const alias of term.aliases) {
      if (t.includes(alias) && (!best || alias.length > best.length)) best = { term, length: alias.length };
    }
  }
  return best?.term ?? null;
}

/** The module an operator asked about, matched on its name or its route. */
export function findModule(q: string): Module | null {
  const t = q.toLowerCase();
  let best: { module: Module; length: number } | null = null;
  for (const m of MODULES) {
    for (const key of [m.name.toLowerCase(), m.id, m.route.replace("/", "")]) {
      if (key.length > 2 && t.includes(key) && (!best || key.length > best.length))
        best = { module: m, length: key.length };
    }
  }
  return best?.module ?? null;
}

/** The tour, as lines. Grouped the way the sidebar groups them. */
export function tour(): string[] {
  const lines = [
    `This platform runs an aerodrome's maintenance and the decisions around it. ${MODULES.length} screens, in ${new Set(MODULES.map((m) => m.group)).size} groups.`,
    "",
  ];
  let group = "";
  for (const m of MODULES) {
    if (m.group !== group) {
      group = m.group;
      lines.push(`— ${group} —`);
    }
    lines.push(`${m.name} (${m.route}) — ${m.what}`);
  }
  lines.push(
    "",
    "Ask me about any one of them and I will say how it works. Ask about a field or an indicator — NOT COMPUTABLE, the A–F grade, GO/NO-GO, priority, severity — and I will tell you what it means and where the rule lives.",
  );
  return lines;
}
